import {
  Alert,
  Box,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Input,
  Label,
  List,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconTourDoorFilled } from '@zillow/constellation-icons';

import { useGetExternalTourProviders } from '../../../../hooks/queries/useGetExternalTourProviders';
import { useScreenSize } from '../../../../hooks/useScreenSize';

import SpinnerContainer from '../../../SpinnerContainer';

import type { ChangeEvent, MutableRefObject } from 'react';
import type { RentalManagerExternalTourDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  externalTour: RentalManagerExternalTourDto;
  onUpdateExternalTour: (label: string, value: string) => void;
  tourUrlErrorMessage?: string | null;
  externalTourInputRef: MutableRefObject<HTMLInputElement | null>;
};

const ExternalTour = ({ externalTour, onUpdateExternalTour, tourUrlErrorMessage, externalTourInputRef }: Props) => {
  const {
    data: externalTourProviders,
    isError,
    isLoading,
    refetch: refetchGetExternalTourProviders,
  } = useGetExternalTourProviders();

  const { isScreenMobile } = useScreenSize();

  const renderProviderCols = () => {
    if (externalTourProviders) {
      const numDisplayCols = isScreenMobile ? 2 : 4;
      const numPerCol = externalTourProviders.length / numDisplayCols;

      let columnStartIdxs = Array(numDisplayCols)
        .fill(0)
        .map((val, idx) => numPerCol * idx);
      if (externalTourProviders.length % numDisplayCols > 0) {
        columnStartIdxs = columnStartIdxs.map((val, idx) => (idx > 0 ? val + 1 : val));
      }

      return columnStartIdxs.map((startIdx, idx) =>
        columnStartIdxs[idx + 1] ? (
          <List.Root key={idx} css={{ minWidth: '136px', maxWidth: '160px', margin: '0 auto', paddingLeft: '400' }}>
            {externalTourProviders.slice(startIdx, columnStartIdxs[idx + 1]).map((name) => (
              <List.Item key={name} css={{ paddingBlock: '0', overflowWrap: 'break-word' }}>
                {name}
              </List.Item>
            ))}
          </List.Root>
        ) : (
          <List.Root key={idx} css={{ minWidth: '136px', maxWidth: '160px', margin: '0 auto', paddingLeft: '400' }}>
            {externalTourProviders.slice(startIdx).map((name) => (
              <List.Item key={name} css={{ paddingBlock: '0', overflowWrap: 'break-word' }}>
                {name}
              </List.Item>
            ))}
          </List.Root>
        ),
      );
    }
  };

  return (
    <Box css={{ marginTop: '200' }}>
      <Box css={{ display: 'flex', alignItems: 'center' }}>
        <Icon size="xl" css={{ color: 'icon.subtle', marginRight: '400' }} render={<IconTourDoorFilled />} />
        <Heading level={2} textStyle="heading-sm">
          Provide the link to your tour
        </Heading>
      </Box>
      <Box css={{ marginBlock: '200' }}>
        <Box css={{ marginBottom: '200' }}>
          <Text>Add a tour link from one of the supported sources:</Text>
        </Box>
        {isLoading && (
          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SpinnerContainer height="auto" />
          </Box>
        )}
        {isError && (
          <Alert
            tone="critical"
            actionButton={<TextButton onClick={() => refetchGetExternalTourProviders()}>Retry now</TextButton>}
          >
            We are having issues loading external tour providers
          </Alert>
        )}
        <Box css={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          {renderProviderCols()}
        </Box>
      </Box>
      <FormField
        css={{ marginBlock: '300' }}
        required={true}
        label={<Label>Tour name</Label>}
        control={
          <Input
            placeholder="New tour"
            value={externalTour.label}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onUpdateExternalTour('label', e.target.value)}
          />
        }
        description={<FormHelp>Modify to a specific tour name (example: &quot;large 1 bedroom&quot;)</FormHelp>}
      />
      <FormField
        css={{ marginBlock: '300' }}
        required={true}
        label={<Label>Tour link</Label>}
        control={
          <Input
            value={externalTour.tourUrl}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onUpdateExternalTour('tourUrl', e.target.value)}
            ref={externalTourInputRef}
            data-testid="SelectTourModal-ExternalTour-tourUrl"
          />
        }
        error={!!tourUrlErrorMessage}
        description={tourUrlErrorMessage ? <FormHelp>{tourUrlErrorMessage}</FormHelp> : null}
      />
    </Box>
  );
};

export default ExternalTour;
